/**
 * Studio implementation of `AuthProvider` (task_111).
 *
 * Studio is the identity provider. There is no email/password form in
 * Designer: `signIn()` redirects to Studio's hosted login, and the resulting
 * `gml_sso` session cookie (HttpOnly, `Domain=.graymatterlab.ai`) travels
 * automatically with every same-site request. Because the cookie is HttpOnly,
 * JS cannot read it — auth state is resolved by calling Designer's
 * `/api/v1/auth/me`, which validates the cookie via the backend provider.
 *
 * `getToken()` returns null: there is no Bearer token on the Studio path, so
 * HTTP adapters must send `credentials: 'include'` and omit the Authorization
 * header when the token is null.
 *
 * Mirrors Hub's frontend swap (adk-workshop-training, PR #66).
 */

import type { AuthProvider, AuthUser } from './AuthProvider';

const STUDIO_LOGIN_URL: string = import.meta.env.VITE_STUDIO_LOGIN_URL ?? '';

// Reach Designer's backend the same way every other client does. In prod
// VITE_API_BASE_URL is the absolute API origin (https://api.designer…/api/v1);
// in dev it's unset and the dev server proxies the relative /api/v1. A bare
// relative '/api/v1/...' breaks in prod: Firebase Hosting's SPA catch-all
// returns index.html (200, HTML) instead of proxying to the backend.
const API_BASE: string = import.meta.env.VITE_API_BASE_URL || '/api/v1';

interface MeResponse {
  uid: string;
  email: string | null;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  picture: string | null;
  email_verified: boolean;
  system_role: string | null;
}

export class StudioAuthProvider implements AuthProvider {
  private currentUser: AuthUser | null = null;
  private listeners = new Set<(user: AuthUser | null) => void>();
  private hasResolved = false;
  private refreshInFlight: Promise<void> | null = null;

  /** Redirect to Studio's hosted login. No local form. */
  async signIn(): Promise<AuthUser | void> {
    this.redirectToLogin();
  }

  private redirectToLogin(): void {
    if (!STUDIO_LOGIN_URL) {
      // Misconfiguration — fail loudly rather than redirect to a blank URL.
      throw new Error(
        'VITE_STUDIO_LOGIN_URL is not set; cannot start Studio login.'
      );
    }
    const target = `${STUDIO_LOGIN_URL}?return=${encodeURIComponent(
      window.location.href
    )}`;
    window.location.href = target;
  }

  /** Clear the shared Studio session, then land on Studio's logged-out page. */
  async signOut(): Promise<void> {
    let logoutUrl: string | null = null;
    try {
      const res = await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = (await res.json().catch(() => null)) as {
          logout_url?: string | null;
        } | null;
        logoutUrl = data?.logout_url ?? null;
      }
    } finally {
      this.setUser(null);
    }
    if (logoutUrl) {
      window.location.href = logoutUrl;
    }
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUser;
  }

  /** No Bearer token on the Studio path — the session cookie carries auth. */
  async getToken(): Promise<string | null> {
    return null;
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    this.listeners.add(callback);
    // Only emit synchronously once we've actually resolved state, to avoid a
    // null → user "logged-out flash" on first subscribe. Then ensure a single
    // shared refresh runs (subscribers share one /me round-trip).
    if (this.hasResolved) {
      callback(this.currentUser);
    }
    void this.refresh();
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Re-resolve the current user from Designer's backend. Concurrent callers
   * share a single in-flight request rather than each firing their own /me.
   */
  private refresh(): Promise<void> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    this.refreshInFlight = this.doRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async doRefresh(): Promise<void> {
    try {
      const res = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = (await res.json()) as MeResponse;
        this.setUser({
          uid: data.uid,
          email: data.email ?? null,
          displayName: data.name ?? null,
          firstName: data.first_name ?? null,
          lastName: data.last_name ?? null,
          photoURL: data.picture ?? null,
          emailVerified: data.email_verified ?? true,
          systemRole: data.system_role ?? null,
        });
        return;
      }
      if (res.status === 401 || res.status === 403) {
        // Genuinely unauthenticated → logged out.
        this.setUser(null);
        return;
      }
      // 5xx / other: a transient backend or upstream blip. Do NOT force a
      // logout on a valid session; leave current state and let a later
      // refresh recover. Still notify so `loading` settles — otherwise a
      // first-load failure hangs the app on a perpetual spinner.
      this.markResolved();
    } catch {
      // Network error — same as a transient server error: don't sign out,
      // but settle loading.
      this.markResolved();
    }
  }

  /**
   * Settle the resolved flag and notify subscribers with the *current* user
   * (no state change). Used on transient failures so the app stops loading
   * without forcing a logout. `setUser` is for actual state transitions.
   */
  private markResolved(): void {
    this.hasResolved = true;
    this.listeners.forEach((cb) => cb(this.currentUser));
  }

  private setUser(user: AuthUser | null): void {
    this.currentUser = user;
    this.hasResolved = true;
    this.listeners.forEach((cb) => cb(user));
  }
}
